import React, { Dispatch, ReactNode, SetStateAction, useCallback } from "react";
import IconBadge from "../badges/IconBadge";
import { MoveHorizontal, TrendingDown, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { VISIT_DURATION_TYPES_IDS } from "../../constants/common";

interface DashboardCardProps {
  data: {
    id: number;
    key: number;
    title: string;
    count: number;
    icon: ReactNode;
    notification: number;
  };
  setTabIndex: Dispatch<SetStateAction<number>>;
  refetch: () => void;
  setSelectedVisitDurationType?: Dispatch<SetStateAction<number>>;
}

const DashboardCard = ({
  data,
  setTabIndex,
  refetch,
  setSelectedVisitDurationType,
}: DashboardCardProps) => {
  const { count, title, icon, notification, key, id } = data;

  const { t } = useTranslation();

  const handleCardClick = useCallback(
    (cardKey: number) => {
      setTabIndex(cardKey - 1);
      refetch();

      if (setSelectedVisitDurationType) {
        if (id === 5) {
          setSelectedVisitDurationType(VISIT_DURATION_TYPES_IDS.ONE_DAY);
        } else if (id === 6) {
          setSelectedVisitDurationType(VISIT_DURATION_TYPES_IDS.MULTIPLE_DAYS);
        } else {
          setSelectedVisitDurationType(0);
        }
      }
    },
    [setTabIndex, refetch, setSelectedVisitDurationType, id]
  );

  return (
    <div
      onClick={() => handleCardClick(key)}
      className="flex flex-col bg-white px-5 py-4 xl:p-3 rounded-[15px] gap-1 cursor-pointer hover:ring-2 hover:ring-purple-primary"
    >
      <div className="flex items-center justify-between">
        <h1 className="text-purple-primary font-light text-3xl">{count}</h1>

        <IconBadge icon={icon} />
      </div>

      <div className="flex flex-col gap-[2px]">
        <h1 className="text-purple-primary">{title}</h1>

        <div className="flex items-center text-purple-primary gap-[7.55px]">
          <div className="rounded-full flex items-center justify-center w-[17px] h-[17px] bg-purple-primary/10">
            {notification > 0 ? (
              <TrendingUp className="w-[9.36px] h-[9.36px]" color="#43900C" />
            ) : notification < 0 ? (
              <TrendingDown className="w-[9.36px] h-[9.36px]" color="#900C0C" />
            ) : (
              <MoveHorizontal
                className="w-[9.36px] h-[9.36px]"
                color="#403f68"
              />
            )}
          </div>
          <p className="text-[10px] xl:text-xs text-gray-secondary">
            {notification > 0
              ? `${notification}% ${t("increaseThanYesterday")}`
              : notification < 0
              ? `${notification}% ${t("decreaseThanYesterday")}`
              : t("sameAsYesterday")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default React.memo(DashboardCard);
